import { useEffect, useCallback, useState } from 'react';

/**
 * Global audio manager to prevent multiple audio sources from playing simultaneously
 */

type AudioSource = 'frequency-detection' | 'notched-noise' | 'visualization';

class AudioManager {
  private currentSource: AudioSource | null = null;
  private listeners: Map<string, () => void> = new Map();

  /**
   * Request to play audio from a specific source
   * @param source The source requesting to play
   * @returns true if permission granted, false if another source is playing
   */
  requestAudioControl(source: AudioSource): boolean {
    if (this.currentSource === null || this.currentSource === source) {
      this.currentSource = source;
      return true;
    }
    return false;
  }

  /**
   * Release audio control
   * @param source The source releasing control
   */
  releaseAudioControl(source: AudioSource): void {
    if (this.currentSource === source) {
      this.currentSource = null;
      this.notifyListeners();
    }
  }

  /**
   * Force stop all audio (used for navigation)
   */
  stopAllAudio(): void {
    this.currentSource = null;
    this.notifyListeners();
  }

  /**
   * Get the current active audio source
   */
  getCurrentSource(): AudioSource | null {
    return this.currentSource;
  }

  /**
   * Add a listener for audio state changes
   */
  addListener(id: string, callback: () => void): void {
    this.listeners.set(id, callback);
  }

  /**
   * Remove a listener
   */
  removeListener(id: string): void {
    this.listeners.delete(id);
  }

  private notifyListeners(): void {
    this.listeners.forEach(callback => callback());
  }
}

// Create singleton instance
export const audioManager = new AudioManager();

// Custom hook for using audio manager
export const useAudioManager = (source: AudioSource) => {
  const [hasControl, setHasControl] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);

  const requestControl = useCallback(() => {
    const granted = audioManager.requestAudioControl(source);
    setHasControl(granted);
    setIsBlocked(!granted);
    return granted;
  }, [source]);

  const releaseControl = useCallback(() => {
    audioManager.releaseAudioControl(source);
    setHasControl(false);
    setIsBlocked(false);
  }, [source]);

  useEffect(() => {
    const listenerId = `audio-manager-${source}`;
    
    const handleAudioStateChange = () => {
      const currentSource = audioManager.getCurrentSource();
      if (currentSource !== source && hasControl) {
        setHasControl(false);
        setIsBlocked(true);
      }
    };

    audioManager.addListener(listenerId, handleAudioStateChange);

    return () => {
      audioManager.removeListener(listenerId);
      if (hasControl) {
        releaseControl();
      }
    };
  }, [source, hasControl, releaseControl]);

  return {
    hasControl,
    isBlocked,
    requestControl,
    releaseControl,
    currentSource: audioManager.getCurrentSource()
  };
};